import { useParams } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'

import {
  Button,
  CheckboxField,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  SelectField,
  TextareaField,
  TextField,
} from '@/components/ui'
import { useToast } from '@/hooks'
import { useUser } from '@/modules/auth'
import { useHelpActions } from '@/modules/help/store'
import { useProjects } from '@/modules/projects'
import {
  useDialogActions,
  useDialogData,
  useDialogIsOpen,
  useForm,
} from '@/stores'
import { usePendingAttachments } from '../hooks/usePendingAttachments'
import type { SupportTicketPrefill } from '../prefill'
import { useSupportIsMutating, useSupportMutations } from '../store'
import {
  AttachmentPickerButton,
  PendingAttachmentList,
} from './PendingAttachments'

const dialogId = 'support-ticket'

export const SupportTicketDialog = () => {
  const { successToast, errorToast } = useToast()
  const open = useDialogIsOpen(dialogId)
  const { closeDialog } = useDialogActions()
  const { data, errors, setFieldValue, reset } = useForm()
  const prefill = useDialogData() as SupportTicketPrefill | undefined
  const user = useUser()
  const projects = useProjects()
  const { slug } = useParams({ strict: false })
  const { createTicket } = useSupportMutations()
  const isMutating = useSupportIsMutating()
  const { openTickets } = useHelpActions()
  const { files, addFiles, removeFile, clearFiles } = usePendingAttachments()

  const seededRef = useRef(false)
  useEffect(() => {
    if (!open) {
      seededRef.current = false
      clearFiles()
      return
    }
    if (seededRef.current) return
    seededRef.current = true
    const currentProjectName =
      projects.find((project) => project.slug === slug)?.name ?? ''
    setFieldValue('subject', prefill?.subject ?? '')
    setFieldValue('description', prefill?.description ?? '')
    setFieldValue('projectName', prefill?.projectName ?? currentProjectName)
  }, [open, prefill, projects, slug, setFieldValue])

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const subject = (data?.subject ?? '') as string
    const description = (data?.description ?? '') as string
    const projectName = (data?.projectName ?? '') as string
    const consent = (data?.consent ?? false) as boolean

    try {
      const ticket = await createTicket({
        subject,
        description,
        projectName: projectName || null,
        email: user?.email || '',
        userName: user?.user_metadata.displayName || '',
        consent,
        files,
      })

      reset()
      clearFiles()
      closeDialog(true)
      successToast('Ticket submitted', "We'll get back to you shortly.")
      openTickets(ticket.publicId)
    } catch (error) {
      errorToast('Failed to submit ticket', error as Error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Submit a support ticket</DialogTitle>
          <DialogDescription>
            Describe your issue and we'll get back to you. We usually reply and
            resolve it the same business day (Mon–Fri, 9am–5pm EST).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit}>
          <div className="py-4 space-y-4">
            <TextField
              label="Subject"
              id="ticket-subject"
              placeholder="Brief summary of your issue"
              value={data?.subject as string}
              error={errors?.subject}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setFieldValue('subject', event.target.value)
              }
              required
              autoFocus
            />

            <TextareaField
              label="Description"
              id="ticket-description"
              placeholder="Describe your issue in detail"
              value={data?.description as string}
              error={errors?.description}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFieldValue('description', event.target.value)
              }
              rows={5}
              required
            />

            <SelectField
              label="Project"
              id="ticket-project"
              placeholder="Select a project (optional)"
              value={data?.projectName as string}
              onValueChange={(value: string) =>
                setFieldValue('projectName', value)
              }
              options={projects.map((project) => ({
                label: project.name,
                value: project.name,
              }))}
            />

            <PendingAttachmentList files={files} onRemove={removeFile} />

            <CheckboxField
              label="I consent to CounselPro accessing my workspace to investigate this issue (optional)"
              checked={data?.consent as boolean}
              onCheckedChange={(checked) =>
                setFieldValue('consent', checked === true)
              }
            />
          </div>

          <div className="flex items-center justify-between gap-2">
            <AttachmentPickerButton onPick={addFiles} size="sm" />
            <div className="flex gap-2">
              <Button theme="outline" onClick={() => closeDialog()}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isMutating}>
                Submit Ticket
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

SupportTicketDialog.id = dialogId
